import React, { useEffect, useContext, useState, useMemo } from 'react';
import styles from './ViewProfile.module.css';
import { RootContext } from '../../hoc/RootContext/RootContext';
import axios from '../../utils/axios';
import { Image } from 'react-bootstrap';
import { Button } from 'react-bootstrap';
import logoIcon from './../../assets/images/icons/logo-icon.svg';
import { ReactComponent as AutocadIcon } from './../../assets/images/general-icons/autocad-icon.svg';
import { ScrollMenu, PageTitle, Analytics } from '@project/components';
import ProfileImages from '../ProfileImages/ProfileImages';
import ProfileAssetSection from '../ProfileAssetSection/ProfileAssetSection';
import ProfileNonAssetSection from '../ProfileNonAssetSection/ProfileNonAssetSection'
import ProfileSection from '../../components/ProfileSection/ProfileSection';
import ProfileShare from '../ProfileShare/ProfileShare';
import ViewMap from '../ViewMap/ViewMap';
import { ASSET_SECTIONS, ASSET_SECTION_LABELS } from '../../constants';
import FloorplanSection from '../FloorplanSection/FloorplanSection';
import BasicInfoSection from '../BasicInfoSection/BasicInfoSection';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import { FaRegFilePdf } from 'react-icons/fa';
import ViewProfileFloors from '../ViewProfileFloors/ViewProfileFloors';
import { useLocation, useParams } from 'react-router-dom';
import themes from './themes';
import { useIntl } from 'react-intl';
import { usePropertyContact } from './hooks'
import layoutSchema from '../SpaceProfile/layoutSchema';

const classNames = require('classnames');

const useQuery = () => new URLSearchParams(useLocation().search);

export const ViewProfile = props => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  const params = useParams();
  const guid = params.guid;
  const privateKey = params.privateKey;
  const [assets, setAssets] = useState([]);
  const [pageLoadError, setPageLoadError] = useState(null);
  const [assetImagesState, setAssetImagesState] = useState({ isOpen: false, images: [], index: 0 })
  const [floorData, setFloorData] = useState([]);
  const [parentProfile, setParentProfile] = useState(null);
  const [fieldSettings, setFieldSettings] = useState(null);
  const [settings, setSettings] = useState(null);
  const [spaceProfilePhotos, setSpaceProfilePhotos] = useState(null);
  const [propertyRecord, setPropertyRecord] = useState(null)
  const [spaceAssignment, setSpaceAssignment] = useState(null)

  const [propertyName, setPropertyName] = useState(null);
  const [companyName, setCompanyName] = useState(null);
  const [space, setSpace] = useState(null);
  const [contacts, setContacts] = useState([])

  const [dynamicSections, setDynamicSections] = useState([])
  const [menuSections, setMenuSections] = useState([])

  const [gotAssets, setGotAssets] = useState(false)

  const {
    setViewerProfile,
    viewerProfile: profile
  } = useContext(RootContext);

  const query = useQuery();
  const level = query.get('level');

  useEffect(() => {
    const keyParam = privateKey ? `&k=${privateKey}` : '';

    const retrieveProfile = async () => {
      try {
        const levelParam = level ? `&level=${level}` : '';
        const result = await axios.get(`/public/space-profiles/${guid}${levelParam || keyParam ? '?' : ''}${levelParam}${keyParam}`);
        if (result.status === 200) {
          const data = result.data.record;
          setViewerProfile(data);
          setFloorData(result.data.floors);
          setSettings(result.data.settings);
          if (!privateKey) setFieldSettings(result.data.settings.fieldSettings);
          if (result.data.parentRecord) setParentProfile(result.data.parentRecord);

          setCompanyName(data.company);
          setPropertyName(data.property);
          setSpace(guid);

          retrieveAssets(privateKey ? data.guid.split('').reverse().join('') : data.guid);
          retrieveSpaceProfilePhotos(privateKey ? data.guid.split('').reverse().join('') : data.guid)
          retrievePropertyRecord(data.property, data.company);
          retrieveSpaceAssignment(data.property, data.company, data.asset_guid);
        }
      } catch (e) {
        if (!e.response) return;
        if (e.response.status === 401) {
          setPageLoadError('Profile not Published');
        }
        if (e.response.status === 404) {
          setPageLoadError('Profile not found');
        }
      }
      // TODO: Redirect to an error page for 404 (no profile found) or 401 (not published)
    }

    const retrieveSpaceProfilePhotos = async (profileGuid) => {
      try {
        const result = await axios.get(`/space-profiles/${profileGuid}/files`);
        if (result.status === 200) {
          setSpaceProfilePhotos(result.data.record);
        }
      } catch (e) {
        if (!e.response) return;
      }
    }

    const retrieveAssets = async (profileGuid) => {
      const result = await axios.get(`/public/space-profile-assets/?spaceProfileGuid=${profileGuid}${keyParam}`);

      if (result.status === 200) {
        setAssets(result.data.records.reduce((acc, asset) => {
          if (!acc[asset.section]) acc[asset.section] = [];
          acc[asset.section].push(asset);
          return acc;
        }, {}));

        setGotAssets(true)
      } else {
        // TODO: Redirect to an error page
      }
    }

    const retrievePropertyRecord = async (propertyName, companyName) => {
      try {
        const result = await axios.get(`/properties/${encodeURIComponent(companyName)}/${encodeURIComponent(propertyName)}`);
        if (result.status === 200) {
          setPropertyRecord(result.data.record)
        }
      } catch (e) {
        if (!e.response) return;
      }
    }

    const retrieveSpaceAssignment = async (propertyName, companyName, assetGuid) => {
      if (!assetGuid) return;
      try {
        const result = await axios.get(`/spaces/asset-guid/${assetGuid}`,
          {params: { company: companyName, property: propertyName }});
        if (result.status === 200 && result.data.record[0]?.address) {
          setSpaceAssignment(result.data.record[0])
        }
      } catch (e) {
        if (!e.response) return;
      }
    }


    retrieveProfile();
  }, [guid, level, privateKey, setViewerProfile]);


  useEffect(() => {
    const retrieveSettings = async () => {
      try {
        const result = await axios.get(`/public/company-settings?name=${encodeURIComponent(companyName)}`);

        if (result.status === 200) {
          setFieldSettings(result.data.record.fieldSettings);
        }
      } catch (e) {
        if (!e.response) return;
      }
    }

    if (!privateKey && companyName) retrieveSettings();
  }, [companyName, privateKey])

  useEffect(() => {

    const setupMenus = () => {

      const tmpDynamicSections = ASSET_SECTIONS
        .filter(section => {
          return assets?.[section.toLowerCase()]?.length
        }).map(_ => _.replace(/ /g, '_'));

      const tmpMenuSections = ['Overview', ...layoutSchema.categories.map(_ => _.id), 'Floorplan', ...tmpDynamicSections];

      setDynamicSections(tmpDynamicSections)
      setMenuSections(tmpMenuSections)
    }

    if (gotAssets) setupMenus()

  }, [gotAssets, assets])

  usePropertyContact(companyName, propertyName, setContacts)


  const dynamicAddress = useMemo(() => {
    return spaceAssignment?.addressObserved || spaceAssignment?.address || profile?.display_address || profile?.address1 || propertyRecord?.address || null
  }, [profile, spaceAssignment, propertyRecord])


  // display the property address
  // note:
  // 'display_address' is a property on the space profile itself. displayAddress is a dynamically retrieved address from the associated space record.

  const Address = ({ profile: { address1, address2, display_address, city, state, postal } }) => {
    return (
      <div>
        <div><span>{intl.formatMessage({ id: 'ViewProfile.address1' }, { address: dynamicAddress || display_address || address1.trim(), city, state, postal })}</span></div>
        <div>{intl.formatMessage({ id: 'ViewProfile.address2' }, { address2 })}</div>
      </div>
    )
  }
  if (pageLoadError) {
    return <div>{pageLoadError}</div>;
  }

  if (!profile || !settings || (!privateKey && !fieldSettings)) return null;

  const mainProfile = parentProfile || profile;

  // consolidate all floorplans into a single array for display
  let floorplans = profile.floorplans && profile.floorplans.length ? [...profile.floorplans] : [];
  if (profile.basic_info.floorplan_upload) floorplans.unshift(profile.basic_info.floorplan_upload);
  if (mainProfile.rpm_svg_url) floorplans.push(mainProfile.rpm_svg_url);
  if (mainProfile.rpm_svg_urls && mainProfile.rpm_svg_urls.length) floorplans = floorplans.concat(mainProfile.rpm_svg_urls);
  if (mainProfile.lod_image) floorplans.unshift(mainProfile.lod_image)

  // determine theme
  const theme = themes[mainProfile.theme || 'default'];

  return (
    <div className={styles.mainContainer}>

      {
        (companyName && propertyName && space) ?
          <Analytics
            ua={process.env.REACT_APP_GOOGLE_UA}
            axios={axios}
            appId={'id360'}
            pageId={'view-profile'}
            recordPageHits={true}
            category={'ID360'}
            name={'View Profile'}
            title={'ID360 View Profile'}
            company={companyName}
            property={propertyName}
            space={space}
          /> : null
      }

      <div className={styles.imagesContainer}>
        <ProfileImages profile={profile} parentProfile={parentProfile} />
      </div>
      <div className={classNames(styles.sideContainer, 'd-flex bg-white flex-column p-0 border-left')}>
        {/* Sidebar compoenent here */}
        <div style={{ backgroundColor: theme.headerColor }} className={classNames(styles.header, 'd-flex align-items-center justify-content-between px-4')}>
          <div className='d-flex align-items-center'>
            {settings.showCompanyLogoEnabled !== false ?
              <Image className={`${styles.logoImage} data-company-logo`} src={mainProfile.companyLogo || logoIcon} />
              : null}
          </div>
          <div className='d-flex align-items-center'>
            {profile.basic_info.profile_pdf_upload || profile.profile_pdf ?
              <a href={profile.basic_info.profile_pdf_upload || profile.profile_pdf} download target="blank" style={{ color: theme.headerIconColor }}>
                <FaRegFilePdf className="mr-4" style={{ fontSize: '22px', color: theme.pdfIconColor }} /></a> : null}
            {profile.basic_info.autocad_upload ? <a href={profile.basic_info.autocad_upload} download target="blank"><AutocadIcon style={{ fill: theme.headerIconColor }} className="mr-4" /></a> : null}
            <ProfileShare profile={profile} theme={theme} />
          </div>
        </div>
        <div className={classNames(styles.content, 'p-4')}>
          <div className={`${styles.basicDimensions} data-basic-dimensions`}>
            {profile.basic_info.overall_actual_sq_ft > 0 ? (
              <h4 className='d-inline mr-3'>{profile.basic_info.overall_actual_sq_ft} sqft.</h4>
            ) : (
              (profile.basic_info.actual_sq_ft === 0 || profile.basic_info.actual_sq_ft > 0) ? (
                <h4 className='d-inline mr-3'>{profile.basic_info.actual_sq_ft} sqft.</h4>
              ) : null
            )}

            {profile.basic_info.total_width_of_space ? <p><strong>{Math.floor(profile.basic_info.total_width_of_space / 12)}' {Math.floor(profile.basic_info.total_width_of_space % 12)}"</strong> {t('ViewProfile.width')}</p> : null}
            {profile.basic_info.total_width_of_space && profile.basic_info.total_depth_of_space ? <div className="ml-2 mr-2"> | </div> : null}
            {profile.basic_info.total_depth_of_space ? <p><strong>{Math.floor(profile.basic_info.total_depth_of_space / 12)}' {Math.floor(profile.basic_info.total_depth_of_space % 12)}"</strong> {t('ViewProfile.depth')}</p> : null}
          </div>
          <div className={classNames(styles.profileAddress, 'my-2')}>
            <div>{profile.property_display_name || profile.property}</div>
            <Address profile={profile} />
          </div>
          {profile.dataPoints ? <div className={styles.dataPoints}><Image className={styles.logoImage} src={logoIcon} /> {intl.formatMessage({ id: 'ViewProfile.dataPoints' }, { dataPoints: profile.dataPoints })}</div> : null}
          {!theme.hideContact && settings.contactRepresentativeEnabled !== false ?
            <Button className={classNames(contacts.length ? '' : 'd-none', 'btn-prime-light btn-block btn btn-none my-3')} onClick={() => window.open(`mailto:${contacts.join(';')}?subject=ID360 - ${profile.property} - Unit ${profile.address2}&body=I'd like to learn more about this space.`)}>
              {t('ViewProfile.contact')}
            </Button>
            : null}
          <ViewProfileFloors floorData={floorData} />
        </div>

        <ScrollMenu
          target={"scrollContent"}
          sections={menuSections}
          labelMap={ASSET_SECTION_LABELS}
        />

        <div id="scrollContent" className={classNames(styles.scrollContentContainer, 'p-4')}>
          <ProfileSection section="Overview" label={ASSET_SECTION_LABELS['Overview']}>
            <div className={classNames(styles.overviewContent, 'd-flex justify-sm-content-between flex-md-row flex-column-reverse p-3')}>
              <ViewMap profile={profile} />
              {profile.basic_info.white_box_link ?
                <div className={classNames(styles.whiteBox, 'position-relative mw-100')}>
                  <a href={profile.basic_info.white_box_link} target="_blank" rel="noopener noreferrer" className='d-block'>
                    <iframe title="white_box" src={profile.basic_info.white_box_link} className={classNames(styles.iframe, 'h-100 w-100')}></iframe>
                  </a>
                </div>
                : null}
            </div>
          </ProfileSection>
          <BasicInfoSection profile={profile} fieldSettings={fieldSettings} spaceProfilePhotos={spaceProfilePhotos} setAssetImagesState={setAssetImagesState} />
          <FloorplanSection floorplans={floorplans} />
          {
            dynamicSections.map(section => {
              return (
                <React.Fragment key={`profile${section}`}>
                  <ProfileAssetSection
                    section={section}
                    assets={assets[section.toLowerCase()]}
                    setAssetImagesState={setAssetImagesState}
                    fieldSettings={fieldSettings} />
                  <ProfileNonAssetSection
                    section={section}
                    assets={assets[section.toLowerCase()]}
                  />
                  <div className='my-4 py-3'><hr /></div>
                </React.Fragment>
              )
            })
          }
        </div>
      </div>
      {assetImagesState.isOpen && (
        <Lightbox
          mainSrc={assetImagesState.images[assetImagesState.index]}
          nextSrc={assetImagesState.images[(assetImagesState.index + 1) % assetImagesState.images.length]}
          prevSrc={assetImagesState.images[(assetImagesState.index + assetImagesState.images.length - 1) % assetImagesState.images.length]}
          onCloseRequest={() => setAssetImagesState({ isOpen: false })}
          onMovePrevRequest={() =>
            setAssetImagesState({ ...assetImagesState, index: (assetImagesState.index + assetImagesState.images.length - 1) % assetImagesState.images.length })
          }
          onMoveNextRequest={() =>
            setAssetImagesState({ ...assetImagesState, index: (assetImagesState.index + 1) % assetImagesState.images.length })
          }
        />
      )}
      <PageTitle title={intl.formatMessage({ id: 'ViewProfile.pageTitle' }, { name: profile.property_display_name || profile.property, address2: profile.address2 })} description={intl.formatMessage({ id: 'ViewProfile.description' }, { name: profile.property_display_name || profile.property, address2: profile.address2 })} />
    </div>
  )
}
